import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, CalendarRange, Users, ClipboardList, CheckCircle2, Clock, Send, Video } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';

const STATE_PILL = {
  draft: 'bg-gray-100 text-gray-500', submitted: 'bg-amber-50 text-amber-700',
  changes_requested: 'bg-red-50 text-red-500', approved: 'bg-sky-50 text-sky-600',
  sent: 'bg-emerald-50 text-emerald-600',
};
const STATE_LABEL = {
  draft: 'Draft', submitted: 'With the school', changes_requested: 'Changes requested',
  approved: 'Approved by school', sent: 'Shared with families',
};
const MARK = { present: 'P', absent: 'A', excused: 'E' };
const MARK_PILL = {
  present: 'bg-emerald-500 text-white', absent: 'bg-red-500 text-white',
  excused: 'bg-amber-400 text-white',
};

/** Confirmed interns for one programme: joining instructions, attendance and completion notes. */
export default function PartnerInternsPanel({ programId, focus, onFocusHandled, onLoaded, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [marking, setMarking] = useState(null);
  const [markingDay, setMarkingDay] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/partner/interns');
      setData(r.data);
      onLoaded?.(r.data);
    } catch { setData(null); }
    setLoading(false);
  }, [onLoaded]);
  useEffect(() => { load(); }, [load]);

  const groups = (data?.groups || []).filter(g => !programId || g.program_id === programId);

  // Overview sends the partner straight to a day's attendance or a pending completion note
  const handledRef = React.useRef('');
  useEffect(() => {
    if (!focus || loading || !data) return;
    const key = `${focus.type}-${focus.opportunityId}-${focus.day || ''}`;
    if (handledRef.current === key) return;
    const opp = (data.groups || [])
      .filter(g => !programId || g.program_id === programId)
      .flatMap(g => g.opportunities)
      .find(o => o.id === focus.opportunityId);
    if (!opp) return;
    handledRef.current = key;
    if (readOnly) return;
    if (focus.type === 'attendance') setMarkingDay({ opportunity: opp, day: focus.day });
    if (focus.type === 'feedback') {
      const student = opp.students.find(s => !s.withdrawn && !s.completion.company_status);
      if (student) setFeedback({ student });
    }
    onFocusHandled?.();
  }, [focus, loading, data, programId, onFocusHandled]);

  if (loading) return (
    <div className="py-16 text-center" data-testid="partner-interns-loading">
      <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#F97D6A]" />
    </div>
  );

  return (
    <div data-testid="partner-interns">
      {readOnly && groups.length > 0 && (
        <p className="text-[11px] text-[#6B7280] bg-[#F8F9FA] rounded-xl px-3 py-2 mb-3 inline-flex items-center gap-1.5" data-testid="interns-read-only">
          <Clock className="h-3.5 w-3.5 text-[#9CA3AF]" />
          This programme is closed, so these records are read-only. Nothing can be marked or submitted now.
        </p>
      )}
      {groups.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-12 text-center" data-testid="interns-empty">
          <Users className="h-7 w-7 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1F2937] font-['Outfit']">No confirmed interns yet</p>
          <p className="text-sm text-[#9CA3AF] mt-1 max-w-md mx-auto leading-relaxed">
            Once the school confirms a placement with your organisation, the student appears here with their
            attendance and completion note.
          </p>
        </div>
      )}

      {groups.map(g => (
        <div key={`${g.school_id}-${g.program_id}`} className="mt-6 first:mt-0" data-testid={`interns-group-${g.program_id}`}>
          {!programId && (
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.12em]">
              {g.school_name} · {g.program_name}
            </p>
          )}
          {g.opportunities.map(o => {
            const ji = o.joining_instructions;
            return (
              <div key={o.id} className="mt-3 rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid={`intern-opportunity-${o.id}`}>
                <div className="px-5 py-4 flex flex-wrap items-start justify-between gap-3 border-b border-[#F3F4F6]">
                  <div>
                    <p className="text-base font-bold text-[#1F2937] font-['Outfit']">{o.title}</p>
                    <p className="text-sm text-[#6B7280] mt-0.5 flex items-center gap-1.5">
                      <CalendarRange className="h-3.5 w-3.5" />
                      {formatDay(ji.reporting_start_date || o.start_date)} – {formatDay(ji.reporting_end_date || o.end_date)}
                      {' · '}{o.students.length} intern{o.students.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Badge className={`border-0 text-[10px] ${STATE_PILL[ji.status]}`} data-testid={`instructions-state-${o.id}`}>
                      {STATE_LABEL[ji.status]}
                    </Badge>
                    {!readOnly && o.days.some(d => d <= new Date().toISOString().slice(0, 10)) && (
                      <Button size="sm" variant="outline" onClick={() => setMarkingDay({ opportunity: o, day: '' })} className="rounded-lg text-xs h-11 sm:h-8 border-gray-200" data-testid={`mark-day-${o.id}`}>
                        <CalendarRange className="h-3.5 w-3.5 mr-1" />Mark a day
                      </Button>
                    )}
                    {!readOnly && (
                      <Button size="sm" variant="outline" onClick={() => setEditing({ opportunity: o })} className="rounded-lg text-xs h-11 sm:h-8 border-gray-200" data-testid={`edit-instructions-${o.id}`}>
                        <ClipboardList className="h-3.5 w-3.5 mr-1" />Joining instructions
                      </Button>
                    )}
                    {readOnly && ji.status !== 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => setEditing({ opportunity: o })} className="rounded-lg text-xs border-gray-200" data-testid={`view-instructions-${o.id}`}>
                        <ClipboardList className="h-3.5 w-3.5 mr-1" />View joining instructions
                      </Button>
                    )}
                  </div>
                </div>

                {ji.coordinator_note && ji.status === 'changes_requested' && (
                  <p className="px-5 py-2 bg-red-50 text-[11px] text-red-600" data-testid={`instructions-note-${o.id}`}>
                    School asked for changes: “{ji.coordinator_note}”
                  </p>
                )}

                <div className="overflow-x-auto hidden sm:block">
                  <table className="w-full text-left min-w-[700px]">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F7F8F9]">
                        <th className="px-5 py-2 font-semibold">Student</th>
                        <th className="px-3 py-2 font-semibold">Attendance</th>
                        <th className="px-3 py-2 font-semibold">Completion note</th>
                        <th className="px-3 py-2 font-semibold text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.students.map(s => (
                        <tr key={s.id} className={`border-b border-[#F7F8F9] last:border-0 ${s.withdrawn ? 'opacity-60' : ''}`} data-testid={`intern-row-${s.id}`}>
                          <td className="px-5 py-3 align-top">
                            <p className="text-sm font-semibold text-[#1F2937]">{s.student_name}</p>
                            <p className="text-[11px] text-[#9CA3AF]">
                              Grade {s.grade} · {s.withdrawn ? 'withdrawn by the school'
                                : s.participation_status === 'confirmed' ? 'family confirmed' : 'awaiting family'}
                            </p>
                          </td>
                          <td className="px-3 py-3 align-top">
                            <div className="flex items-center gap-1">
                              {s.attendance.days.map(d => (
                                <span key={d.day} title={`${formatDay(d.day)}${d.note ? ` · ${d.note}` : ''}`}
                                  className={`h-5 w-5 rounded text-[10px] font-bold flex items-center justify-center ${d.status ? MARK_PILL[d.status] : d.due ? 'bg-[#F3F4F6] text-[#9CA3AF]' : 'bg-white border border-dashed border-[#E5E7EB] text-[#D1D5DB]'}`}
                                  data-testid={`attendance-cell-${s.id}-${d.day}`}>
                                  {d.status ? MARK[d.status] : d.due ? '·' : ''}
                                </span>
                              ))}
                              <span className="text-[11px] text-[#6B7280] ml-1.5" data-testid={`attendance-summary-${s.id}`}>{s.attendance.summary}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top">
                            {s.completion.company_status ? (
                              <>
                                <Badge className="border-0 text-[10px] bg-sky-50 text-sky-600" data-testid={`feedback-status-${s.id}`}>
                                  {COMPLETION_LABEL[s.completion.company_status] || s.completion.company_status.replace(/_/g, ' ')}
                                </Badge>
                                <p className="text-[10px] text-[#9CA3AF] mt-1">
                                  {s.completion.conduct === 'satisfactory' ? 'Conduct satisfactory' : 'Conduct needs attention'}
                                  {s.completion.corrections?.length ? ` · note updated ${s.completion.corrections.length}×` : ''}
                                </p>
                              </>
                            ) : <span className="text-[11px] text-[#9CA3AF]" data-testid={`feedback-status-${s.id}`}>Not submitted</span>}
                          </td>
                          <td className="px-3 py-3 align-top text-right whitespace-nowrap">
                            {s.withdrawn ? (
                              <Badge className="border-0 text-[10px] bg-gray-100 text-gray-500" data-testid={`withdrawn-${s.id}`}>Withdrawn</Badge>
                            ) : readOnly ? (
                              <Button size="sm" variant="outline" onClick={() => setViewing({ student: s, opportunity: o })}
                                className="rounded-lg text-[11px] h-7 border-gray-200" data-testid={`view-record-${s.id}`}>
                                View record
                              </Button>
                            ) : (
                              <>
                                {(ji.reporting_start_date || o.start_date) <= new Date().toISOString().slice(0, 10) && (
                                  <Button size="sm" variant="outline" onClick={() => setMarking({ student: s, days: s.attendance.days })} className="rounded-lg text-[11px] h-7 border-gray-200 mr-1.5" data-testid={`mark-attendance-${s.id}`}>Attendance</Button>
                                )}
                                {(ji.reporting_end_date || o.end_date) <= new Date().toISOString().slice(0, 10) ? (
                                  <Button size="sm" onClick={() => setFeedback({ student: s })} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-[11px] h-7" data-testid={`give-feedback-${s.id}`}>
                                    {s.completion.company_status ? 'Edit note' : 'Completion'}
                                  </Button>
                                ) : (
                                  <span className="text-[10px] text-[#9CA3AF]" data-testid={`feedback-not-due-${s.id}`}>Note after the last day</span>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Phone view: one card per intern, with the attendance summary rather than
                    a row of squares, and the same dialogs behind each action */}
                <div className="sm:hidden divide-y divide-[#F7F8F9]" data-testid="intern-card-list">
                  {o.students.map(s => (
                    <div key={s.id} className={`px-4 py-3 ${s.withdrawn ? 'opacity-60' : ''}`} data-testid={`intern-card-${s.id}`}>
                      <p className="text-sm font-semibold text-[#1F2937] break-words">{s.student_name}</p>
                      <p className="text-[11px] text-[#9CA3AF]">
                        Grade {s.grade} · {s.withdrawn ? 'withdrawn by the school'
                          : s.participation_status === 'confirmed' ? 'family confirmed' : 'awaiting family'}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#4B5563]" data-testid={`attendance-summary-card-${s.id}`}>
                          {s.attendance.summary}
                        </Badge>
                        {s.completion.company_status ? (
                          <Badge className="border-0 text-[10px] bg-sky-50 text-sky-600" data-testid={`feedback-status-card-${s.id}`}>
                            {COMPLETION_LABEL[s.completion.company_status] || s.completion.company_status.replace(/_/g, ' ')}
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-[#9CA3AF]" data-testid={`feedback-status-card-${s.id}`}>Feedback not submitted</span>
                        )}
                      </div>
                      {s.withdrawn ? (
                        <Badge className="border-0 text-[10px] bg-gray-100 text-gray-500 mt-2" data-testid={`withdrawn-card-${s.id}`}>Withdrawn</Badge>
                      ) : readOnly ? (
                        <Button variant="outline" onClick={() => setViewing({ student: s, opportunity: o })}
                          className="w-full mt-2.5 h-11 rounded-lg text-xs border-gray-200" data-testid={`view-record-card-${s.id}`}>
                          View record
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2 mt-2.5">
                          {(ji.reporting_start_date || o.start_date) <= new Date().toISOString().slice(0, 10) && (
                            <Button variant="outline" onClick={() => setMarking({ student: s, days: s.attendance.days })}
                              className="flex-1 h-11 rounded-lg text-xs border-gray-200" data-testid={`mark-attendance-card-${s.id}`}>
                              Attendance
                            </Button>
                          )}
                          {(ji.reporting_end_date || o.end_date) <= new Date().toISOString().slice(0, 10) ? (
                            <Button onClick={() => setFeedback({ student: s })}
                              className="flex-1 h-11 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" data-testid={`give-feedback-card-${s.id}`}>
                              {s.completion.company_status ? 'Edit note' : 'Completion'}
                            </Button>
                          ) : (
                            <span className="flex-1 text-[10px] text-[#9CA3AF]" data-testid={`feedback-not-due-card-${s.id}`}>Note after the last day</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {editing && <InstructionsDialog opportunity={editing.opportunity} readOnly={readOnly} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {marking && <AttendanceDialog student={marking.student} onClose={() => setMarking(null)} onDone={() => { setMarking(null); load(); }} />}
      {markingDay && <DayAttendanceDialog opportunity={markingDay.opportunity} initialDay={markingDay.day} onClose={() => setMarkingDay(null)} onDone={() => { setMarkingDay(null); load(); }} />}
      {feedback && <FeedbackDialog student={feedback.student} onClose={() => setFeedback(null)} onDone={() => { setFeedback(null); load(); }} />}
      {viewing && <RecordDialog student={viewing.student} opportunity={viewing.opportunity} onClose={() => setViewing(null)} />}
    </div>
  );
}

const COMPLETION_LABEL = {
  completed: 'Completed', partially_completed: 'Partially completed', not_completed: 'Not completed',
};

/** Everything recorded for one intern, kept readable after the programme closes. */
function RecordDialog({ student: s, opportunity, onClose }) {
  const c = s.completion || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="student-record-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">{s.student_name}</DialogTitle>
          <DialogDescription className="text-xs">
            Grade {s.grade} · {opportunity.title} · {formatDay(opportunity.start_date)} – {formatDay(opportunity.end_date)}
          </DialogDescription>
        </DialogHeader>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF] mb-1.5">Attendance</p>
          <p className="text-xs text-[#6B7280] mb-2" data-testid="record-attendance-summary">
            {s.attendance.summary}
            {s.attendance.excused ? ` · ${s.attendance.excused} excused` : ''}
            {s.attendance.absent ? ` · ${s.attendance.absent} absent` : ''}
          </p>
          <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl">
            {s.attendance.days.map(d => (
              <li key={d.day} className="px-3 py-2" data-testid={`record-day-${d.day}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[#1F2937]">{formatDay(d.day)}</p>
                  <Badge className={`border-0 text-[10px] ${d.status === 'present' ? 'bg-emerald-50 text-emerald-600' : d.status === 'excused' ? 'bg-amber-50 text-amber-700' : d.status === 'absent' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                    {d.status ? d.status[0].toUpperCase() + d.status.slice(1) : 'Not marked'}
                  </Badge>
                </div>
                {d.note && <p className="text-[11px] text-[#6B7280] mt-0.5">{d.note}</p>}
                {(d.history || []).filter(h => h.reason).map((h, i) => (
                  <p key={i} className="text-[11px] text-amber-700 mt-0.5">Changed to {h.status}: {h.reason}</p>
                ))}
                {d.marked_by && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Marked by {d.marked_by}</p>}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF] mb-1.5">Completion feedback</p>
          {c.company_status ? (
            <div className="bg-[#FAFBFC] rounded-xl px-3 py-2.5 space-y-1" data-testid="record-completion">
              <p className="text-xs text-[#1F2937]">
                <strong>{COMPLETION_LABEL[c.company_status] || c.company_status}</strong>
                {c.conduct ? ` · conduct ${c.conduct === 'satisfactory' ? 'satisfactory' : 'needs attention'}` : ''}
              </p>
              {c.comments && <p className="text-xs text-[#4B5563] leading-relaxed whitespace-pre-wrap" data-testid="record-comments">“{c.comments}”</p>}
              <p className="text-[10px] text-[#9CA3AF]">
                Submitted by {c.submitted_by}{c.submitted_at ? ` on ${formatDay(c.submitted_at.slice(0, 10))}` : ''}
              </p>
              {(c.corrections || []).map((x, i) => (
                <p key={i} className="text-[10px] text-amber-700">Corrected {formatDay((x.at || '').slice(0, 10))}: {x.note || x.comments}</p>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#9CA3AF]" data-testid="record-no-completion">No completion note was recorded.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200" data-testid="record-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstructionsDialog({ opportunity, onClose, onDone, readOnly = false }) {
  const ji = opportunity.joining_instructions;
  const [form, setForm] = useState({
    reporting_start_date: ji.reporting_start_date || '', reporting_end_date: ji.reporting_end_date || '',
    daily_start_time: ji.daily_start_time || '', daily_end_time: ji.daily_end_time || '',
    mode: ji.mode || 'on_site', workplace_address: ji.workplace_address || '',
    reporting_location: ji.reporting_location || '', supervisor_name: ji.supervisor_name || '',
    supervisor_contact: ji.supervisor_contact || '', items_to_bring: ji.items_to_bring || '',
    dress_code: ji.dress_code || '', agenda: ji.agenda || '',
    additional_instructions: ji.additional_instructions || '',
    meeting_platform: ji.meeting_platform || '', meeting_link: ji.meeting_link || '',
    meeting_passcode: ji.meeting_passcode || '', meeting_notes: ji.meeting_notes || '',
  });
  const [dayLinks, setDayLinks] = useState(() => {
    const byDay = Object.fromEntries((ji.day_links || []).map(d => [d.day, d]));
    return (opportunity.days || []).map(d => ({ day: d, link: byDay[d]?.link || '', note: byDay[d]?.note || '' }));
  });  const [perDay, setPerDay] = useState((ji.day_links || []).length > 0);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const online = form.mode === 'remote' || form.mode === 'hybrid';

  // the per-day rows follow whatever reporting dates the company types in
  useEffect(() => {
    const { reporting_start_date: from, reporting_end_date: to } = form;
    if (!from) return setDayLinks([]);
    const days = [];
    for (let d = new Date(from); d <= new Date(to || from) && days.length < 7; d.setDate(d.getDate() + 1)) {
      days.push(d.toISOString().slice(0, 10));
    }
    setDayLinks(rows => days.map(day => rows.find(r => r.day === day) || { day, link: '', note: '' }));
  }, [form.reporting_start_date, form.reporting_end_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (submit) => {
    setSaving(true);
    try {
      await api.put(`/partner/interns/opportunities/${opportunity.id}/joining-instructions`, {
        ...form, submit,
        day_links: perDay ? dayLinks.filter(d => d.link.trim()) : [],
        meeting_link: perDay ? '' : form.meeting_link,
      });
      toast.success(submit ? 'Sent to the school for review' : 'Saved');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save these instructions'); }
    setSaving(false);
  };

  const field = (key, label, type = 'text') => (
    <div>
      <Label className="text-xs font-medium text-[#1F2937]">{label}</Label>
      <Input type={type} value={form[key]} onChange={e => set(key, e.target.value)} className="mt-1 rounded-xl border-gray-200 h-9 text-sm" data-testid={`ji-${key}`} />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="instructions-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Joining instructions · {opportunity.title}</DialogTitle>
          <DialogDescription className="text-xs">
            {readOnly
              ? 'Kept exactly as the school and families saw them. This programme has closed, so nothing can be changed.'
              : 'Prefilled from your offer. The school reviews these before any family sees them, and they apply to every intern on this internship.'}
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={readOnly}>
        <div className="grid sm:grid-cols-2 gap-3">
          {field('reporting_start_date', 'First reporting date *', 'date')}
          {field('reporting_end_date', 'Last day', 'date')}
          {field('daily_start_time', 'Daily start *', 'time')}
          {field('daily_end_time', 'Daily end *', 'time')}
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Mode *</Label>
            <select value={form.mode} onChange={e => set('mode', e.target.value)} className="mt-1 w-full h-9 rounded-xl border border-gray-200 text-sm px-2 bg-white" data-testid="ji-mode">
              <option value="on_site">On-site</option>
              <option value="hybrid">Hybrid</option>
              <option value="remote">Remote</option>
            </select>
          </div>
          {field('supervisor_name', 'Supervisor name *')}
          {field('supervisor_contact', 'Supervisor contact')}
          {field('dress_code', 'Dress code')}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Where to report{online ? '' : ' *'}</Label>
            <Textarea value={form.reporting_location} onChange={e => set('reporting_location', e.target.value)} placeholder="Gate, floor or reception desk for on-site days" className="mt-1 rounded-xl border-gray-200 min-h-[52px] text-sm resize-none" data-testid="ji-reporting_location" />
          </div>

          {online && (
            <div className="rounded-xl border border-[#DCE7FB] bg-[#F7FAFF] px-3 py-3 space-y-3" data-testid="ji-online-block">
              <div className="flex items-start gap-2">
                <Video className="h-4 w-4 text-[#2A4B8D] mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-[#2A4B8D]">Online joining details *</p>
                  <p className="text-[11px] text-[#5B7BBF] leading-relaxed">
                    Google Meet, Microsoft Teams, Zoom or any valid meeting link. Only the school and the
                    confirmed families see it — it never appears on public listings.
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => setPerDay(false)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${!perDay ? 'bg-[#2A4B8D] text-white' : 'bg-white border border-[#DCE7FB] text-[#5B7BBF]'}`}
                  data-testid="ji-link-mode-common">One link for all days</button>
                <button type="button" onClick={() => setPerDay(true)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${perDay ? 'bg-[#2A4B8D] text-white' : 'bg-white border border-[#DCE7FB] text-[#5B7BBF]'}`}
                  data-testid="ji-link-mode-day">A link for each day</button>
              </div>
              {!perDay ? (
                <div>
                  <Label className="text-xs font-medium text-[#1F2937]">Meeting link</Label>
                  <Input value={form.meeting_link} onChange={e => set('meeting_link', e.target.value)} placeholder="https://meet.google.com/abc-defg-hij" className="mt-1 rounded-xl border-gray-200 h-9 text-sm" data-testid="ji-meeting_link" />
                </div>
              ) : (
                <div className="space-y-2" data-testid="ji-day-links">
                  {dayLinks.map((d, i) => (
                    <div key={d.day} className="grid sm:grid-cols-[110px_1fr] gap-2 items-center">
                      <span className="text-[11px] font-semibold text-[#4B5563]">{formatDay(d.day)}</span>
                      <Input value={d.link} placeholder="https://…" className="rounded-xl border-gray-200 h-9 text-sm"
                        onChange={e => setDayLinks(rows => rows.map((r, j) => j === i ? { ...r, link: e.target.value } : r))}
                        data-testid={`ji-day-link-${d.day}`} />
                    </div>
                  ))}
                  {dayLinks.length === 0 && <p className="text-[11px] text-[#9CA3AF]">Set the reporting dates first to list the days.</p>}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-medium text-[#1F2937]">Passcode (optional)</Label>
                  <Input value={form.meeting_passcode} onChange={e => set('meeting_passcode', e.target.value)} className="mt-1 rounded-xl border-gray-200 h-9 text-sm" data-testid="ji-meeting_passcode" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#1F2937]">Joining notes (optional)</Label>
                  <Input value={form.meeting_notes} onChange={e => set('meeting_notes', e.target.value)} placeholder="e.g. join 5 minutes early, camera on" className="mt-1 rounded-xl border-gray-200 h-9 text-sm" data-testid="ji-meeting_notes" />
                </div>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Workplace address</Label>
            <Textarea value={form.workplace_address} onChange={e => set('workplace_address', e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[52px] text-sm resize-none" data-testid="ji-workplace_address" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">What students should bring</Label>
            <Textarea value={form.items_to_bring} onChange={e => set('items_to_bring', e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[52px] text-sm resize-none" data-testid="ji-items_to_bring" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Day-wise agenda (optional)</Label>
            <Textarea value={form.agenda} onChange={e => set('agenda', e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[52px] text-sm resize-none" data-testid="ji-agenda" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Anything else (optional)</Label>
            <Textarea value={form.additional_instructions} onChange={e => set('additional_instructions', e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[52px] text-sm resize-none" data-testid="ji-additional_instructions" />
          </div>
        </div>
        </fieldset>
        <DialogFooter className="gap-2 sm:justify-between">
          {readOnly ? (
            <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200 text-sm" data-testid="close-instructions-btn">Close</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => save(false)} disabled={saving} className="rounded-xl text-sm text-[#6B7280]" data-testid="save-instructions-btn">Save draft</Button>
              <Button onClick={() => save(true)} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="submit-instructions-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Submit to school
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttendanceDialog({ student, onClose, onDone }) {
  const [days, setDays] = useState(student.attendance.days);
  const [saving, setSaving] = useState(null);
  const [pending, setPending] = useState(null);     // { day, status, needsReason }
  const [text, setText] = useState('');

  const save = async (day, status, reason, note) => {
    setSaving(day);
    try {
      const r = await api.post('/partner/interns/attendance', {
        application_id: student.id, day, status, note: note || null, reason: reason || null });
      setDays(r.data.days);
      setPending(null);
      setText('');
      toast.success(`${formatDay(day)} marked ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record attendance'); }
    setSaving(null);
  };

  const choose = (day, status) => {
    const existing = days.find(d => d.day === day);
    const needsReason = Boolean(existing?.status && existing.status !== status);
    if (!needsReason && status === 'present') return save(day, status);
    setText('');
    setPending({ day, status, needsReason });
  };

  return (
    <Dialog open onOpenChange={() => { onDone(); }}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="attendance-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Attendance · {student.student_name}</DialogTitle>
          <DialogDescription className="text-xs">
            Mark each internship day. A changed day asks for a reason and is kept in the record.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl">
          {days.map(d => (
            <li key={d.day} className="px-3 py-2.5" data-testid={`attendance-day-${d.day}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#1F2937]">{formatDay(d.day)}</p>
                  {d.note && <p className="text-[10px] text-[#6B7280]">“{d.note}”</p>}
                  {d.history?.length > 1 && d.history[d.history.length - 1].reason && (
                    <p className="text-[10px] text-[#6B7280]">Changed: “{d.history[d.history.length - 1].reason}”</p>
                  )}
                  {d.marked_by && <p className="text-[10px] text-[#9CA3AF]">{d.marked_by}</p>}
                </div>
                {d.due ? (
                  <div className="flex gap-1">
                    {['present', 'absent', 'excused'].map(st => (
                      <Button key={st} size="sm" variant={d.status === st ? 'default' : 'outline'}
                        disabled={saving === d.day}
                        onClick={() => choose(d.day, st)}
                        className={`rounded-lg text-[10px] h-10 sm:h-7 px-3 sm:px-2 ${d.status === st ? 'bg-[#1F2937] text-white' : 'border-gray-200'}`}
                        data-testid={`mark-${d.day}-${st}`}>
                        {st === 'present' ? 'Present' : st === 'absent' ? 'Absent' : 'Excused'}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <span className="text-[10px] text-[#9CA3AF] inline-flex items-center gap-1" data-testid={`not-due-${d.day}`}>
                    <Clock className="h-3 w-3" />Not yet due
                  </span>
                )}
              </div>

              {pending?.day === d.day && (
                <div className="mt-2 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid={`attendance-reason-${d.day}`}>
                  <Label className="text-[11px] font-medium text-[#1F2937]">
                    {pending.needsReason
                      ? `Why is this changing to ${pending.status}? *`
                      : `Note for this ${pending.status} day (optional)`}
                  </Label>
                  <Textarea value={text} onChange={e => setText(e.target.value)}
                    className="mt-1 rounded-xl border-gray-200 min-h-[56px] text-xs resize-none"
                    data-testid={`attendance-reason-input-${d.day}`} />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => { setPending(null); setText(''); }}
                      className="rounded-lg text-[11px] h-7 border-gray-200">Cancel</Button>
                    <Button size="sm" disabled={saving === d.day || (pending.needsReason && !text.trim())}
                      onClick={() => pending.needsReason
                        ? save(d.day, pending.status, text.trim(), null)
                        : save(d.day, pending.status, null, text.trim())}
                      className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-[11px] h-7 disabled:opacity-40"
                      data-testid={`attendance-reason-save-${d.day}`}>Save</Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onDone} className="rounded-xl border-gray-200">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DayAttendanceDialog({ opportunity, initialDay, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const dueDays = (opportunity.days || []).filter(d => d <= today);
  const [day, setDay] = useState(initialDay && dueDays.includes(initialDay) ? initialDay : dueDays[dueDays.length - 1] || '');
  const [students, setStudents] = useState(opportunity.students.filter(s => !s.withdrawn));
  const [saving, setSaving] = useState(null);
  const [pending, setPending] = useState(null);     // { studentId, status, needsReason }
  const [text, setText] = useState('');

  const statusFor = (s) => s.attendance.days.find(d => d.day === day)?.status || null;

  const save = async (student, status, reason, note) => {
    setSaving(student.id);
    try {
      const r = await api.post('/partner/interns/attendance', {
        application_id: student.id, day, status, note: note || null, reason: reason || null });
      setStudents(rows => rows.map(s => s.id === student.id
        ? { ...s, attendance: { ...s.attendance, days: r.data.days } } : s));
      setPending(null);
      setText('');
      toast.success(`${student.student_name} marked ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record attendance'); }
    setSaving(null);
  };

  const choose = (student, status) => {
    const existing = statusFor(student);
    const needsReason = Boolean(existing && existing !== status);
    if (!needsReason && status === 'present') return save(student, status);
    setText('');
    setPending({ studentId: student.id, status, needsReason });
  };

  const remaining = students.filter(s => !statusFor(s)).length;

  return (
    <Dialog open onOpenChange={onDone}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="day-attendance-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Attendance · {opportunity.title}</DialogTitle>
          <DialogDescription className="text-xs">
            Mark everyone for one day. Changing a day that is already marked asks for a reason and stays in the record.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 flex-wrap" data-testid="day-attendance-days">
          {dueDays.map(d => (
            <button key={d} onClick={() => { setDay(d); setPending(null); }}
              className={`px-2.5 py-1 min-h-[40px] sm:min-h-0 rounded-full text-[11px] font-semibold border transition-colors ${day === d ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`}
              data-testid={`day-attendance-day-${d}`}>
              {formatDay(d)}{d === today ? ' · today' : ''}
            </button>
          ))}
          {dueDays.length === 0 && <p className="text-[11px] text-[#9CA3AF]">This internship has not started yet.</p>}
        </div>

        <p className="text-[11px] text-[#6B7280]" data-testid="day-attendance-remaining">
          {remaining === 0 ? 'Everyone is marked for this day.' : `${remaining} intern${remaining === 1 ? '' : 's'} still to mark.`}
        </p>

        <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl">
          {students.map(s => {
            const current = statusFor(s);
            return (
              <li key={s.id} className="px-3 py-2.5" data-testid={`day-attendance-student-${s.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#1F2937]">{s.student_name}</p>
                    <p className="text-[10px] text-[#9CA3AF]">Grade {s.grade} · {current ? `marked ${current}` : 'not marked'}</p>
                  </div>
                  <div className="flex gap-1">
                    {['present', 'absent', 'excused'].map(st => (
                      <Button key={st} size="sm" variant={current === st ? 'default' : 'outline'}
                        disabled={saving === s.id || !day}
                        onClick={() => choose(s, st)}
                        className={`rounded-lg text-[10px] h-10 sm:h-7 px-3 sm:px-2 ${current === st ? 'bg-[#1F2937] text-white' : 'border-gray-200'}`}
                        data-testid={`day-mark-${s.id}-${st}`}>
                        {st === 'present' ? 'Present' : st === 'absent' ? 'Absent' : 'Excused'}
                      </Button>
                    ))}
                  </div>
                </div>

                {pending?.studentId === s.id && (
                  <div className="mt-2 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid={`day-attendance-reason-${s.id}`}>
                    <Label className="text-[11px] font-medium text-[#1F2937]">
                      {pending.needsReason
                        ? `Why is this changing to ${pending.status}? *`
                        : `Note for this ${pending.status} day (optional)`}
                    </Label>
                    <Textarea value={text} onChange={e => setText(e.target.value)}
                      className="mt-1 rounded-xl border-gray-200 min-h-[56px] text-xs resize-none"
                      data-testid={`day-attendance-reason-input-${s.id}`} />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button size="sm" variant="outline" onClick={() => { setPending(null); setText(''); }}
                        className="rounded-lg text-[11px] h-7 border-gray-200">Cancel</Button>
                      <Button size="sm" disabled={saving === s.id || (pending.needsReason && !text.trim())}
                        onClick={() => pending.needsReason
                          ? save(s, pending.status, text.trim(), null)
                          : save(s, pending.status, null, text.trim())}
                        className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-[11px] h-7 disabled:opacity-40"
                        data-testid={`day-attendance-reason-save-${s.id}`}>Save</Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onDone} className="rounded-xl border-gray-200" data-testid="day-attendance-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({ student, onClose, onDone }) {
  const c = student.completion;
  const [status, setStatus] = useState(c.company_status || 'completed');
  const [conduct, setConduct] = useState(c.conduct || 'satisfactory');
  const [comments, setComments] = useState(c.comments || '');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const missing = !comments.trim();

  const submit = async () => {
    setTouched(true);
    if (missing) {
      toast.error('Please add supervisor comments before submitting');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/partner/interns/${student.id}/feedback`, { status, conduct, comments });
      toast.success('Completion note sent to the school');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit this note'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Completion note · {student.student_name}</DialogTitle>
          <DialogDescription className="text-xs">
            Attendance recorded: {student.attendance.summary}. The school makes the final completion decision.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Internship status</Label>
            <div className="flex gap-1.5 mt-1.5">
              {[['completed', 'Completed'], ['partially_completed', 'Partially completed'], ['not_completed', 'Not completed']].map(([v, l]) => (
                <button key={v} onClick={() => setStatus(v)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${status === v ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200'}`} data-testid={`feedback-status-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Professional conduct</Label>
            <div className="flex gap-1.5 mt-1.5">
              {[['satisfactory', 'Satisfactory'], ['needs_attention', 'Needs attention']].map(([v, l]) => (
                <button key={v} onClick={() => setConduct(v)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${conduct === v ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200'}`} data-testid={`feedback-conduct-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Supervisor comments *</Label>
            <Textarea value={comments} onChange={e => setComments(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={status === 'completed'
                ? 'How did the student get on — strengths, contribution, anything the school should know'
                : status === 'partially_completed'
                  ? 'Please explain what was left incomplete and why'
                  : 'Please explain why the internship was not completed'}
              className={`mt-1 rounded-xl min-h-[64px] text-sm resize-none ${touched && missing ? 'border-red-400 focus-visible:ring-red-300' : 'border-gray-200'}`}
              data-testid="feedback-comments" />
            {touched && missing ? (
              <p className="text-[11px] text-red-500 mt-1" data-testid="feedback-comments-error">
                Supervisor comments are required{status === 'completed' ? '' : ' — tell the school what was incomplete and why'}.
              </p>
            ) : (
              <p className="text-[11px] text-[#9CA3AF] mt-1">
                {status === 'completed'
                  ? 'The school reads this before verifying the internship.'
                  : 'Explain what was incomplete or why, so the school can follow up with the family.'}
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving || (touched && missing)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="submit-feedback-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Submit note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
